import { ReactNode } from "react";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import { t } from "i18next";
import { Typography } from "@material-tailwind/react";
import Loading from "../loading/Loading";

interface GenericListLayoutProps {
  entityName: string; // Nom de l'entité (ex: "Parts", "Customers")
  path?: string; // Chemin pour le breadcrumb (ex: "/parts")
  isLoading?: boolean; // Indicateur de chargement
  headerActions?: ReactNode; // Actions dans l'en-tête (boutons d'export, etc.)
  filterDrawer?: ReactNode; // Bouton d'ouverture du drawer de filtre
  filterBadges?: ReactNode; // Badges de filtres actifs
  createDrawer?: ReactNode; // Drawer de création (optionnel)
  content: ReactNode; // Contenu principal (tableau)
  pagination?: ReactNode; // Composant de pagination
  className?: string; // Classes CSS additionnelles
  showBreadcrumb?: boolean; // Afficher ou non le breadcrumb
  hideHeader?: boolean; // Masquer l'en-tête (titre et actions)
  noPadding?: boolean; // Supprimer le padding par défaut
}

/**
 * GenericListLayout - Layout générique pour les vues de liste
 *
 * Ce composant sert de wrapper pour les différentes vues de liste (Parts, Workorders, etc.)
 * Il fournit une structure commune avec breadcrumb, titre, actions, filtres, contenu et pagination
 */
export function GenericListLayout({
  entityName,
  path,
  isLoading = false,
  headerActions,
  filterDrawer,
  filterBadges,
  createDrawer,
  content,
  pagination,
  className = "",
  showBreadcrumb = true,
  hideHeader = false,
  noPadding = false,
}: GenericListLayoutProps) {
  const baseClassName = `${className.toLowerCase()}`;
  const fullClassName = `${baseClassName} ${className} ${noPadding ? "!p-0" : ""}`.trim();

  const translatedEntityName = (() => {
    try {
      const translation = t(entityName);
      return typeof translation === "string" ? translation : entityName;
    } catch (err) {
      return entityName;
    }
  })();

  return (
    <div className={fullClassName}>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {showBreadcrumb && path && (
            <Breadcrumb
              path={[
                {
                  name: translatedEntityName,
                  path,
                },
              ]}
            />
          )}

          {!hideHeader && (
            <div className={`${baseClassName}-header mb-4`}>
              <div className="flex justify-between items-center w-full">
                <div className={`${baseClassName}-title`}>
                  <Typography variant={showBreadcrumb ? "h2" : "h4"}>
                    {translatedEntityName}
                  </Typography>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-grow overflow-x-auto">
                    {filterBadges}
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-2">
                    {filterDrawer}
                    {headerActions}
                    {createDrawer}
                  </div>
                </div>
              </div>
            </div>
          )}

          {content}

          {pagination}
        </>
      )}
    </div>
  );
}
